<script setup lang="ts">
/**
 * 源站 .side-menu-root：面板在 header 下 / tabbar 上，#232325，overlay rgba(0,0,0,.55)。
 * 450 宽实测：panel 450×753.6 / promo 426×72.1 r7.2 / nav 45.6 / lang 45.6 #15161C。
 */
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  catalogNavNodes,
  isOroMxChromeTab,
  toOroMxCatalogTab,
} from '@/features/game/oro-mx-catalog';
import { loadOroMxHomeCatalog } from '@/features/game/oro-mx-home-catalog';
import { useSupportChat } from '@/features/support/use-support-chat';
import { t } from '@/shared/i18n/messages';
import { oroMxAssetPaths } from '@/shared/theme/oro-mx-home-data';
import {
  oroMxMenuAssets,
  oroMxMenuCasinoSubs,
  oroMxMenuNav,
  oroMxMenuPromos,
} from '@/shared/theme/oro-mx-page-data';
import OroMxLangPicker from '@/widgets/app-shell/OroMxLangPicker.vue';
import { useAuthModalStore } from '@/stores/auth-modal-store';
import { useOroMxShellStore } from '@/stores/oro-mx-shell-store';
import { useSessionStore } from '@/stores/session-store';

const route = useRoute();
const router = useRouter();
const shell = useOroMxShellStore();
const authModal = useAuthModalStore();
const sessionStore = useSessionStore();
const supportChat = useSupportChat();
const sprite = oroMxAssetPaths.sprite;
const casinoOpen = ref(false);
const casinoSubs = computed(() => [
  ...oroMxMenuCasinoSubs,
  ...catalogNavNodes(shell.navNodes).map(toOroMxCatalogTab),
]);

watch(
  () => shell.menuOpen,
  (open) => {
    if (!open) return;
    supportChat.preload();
    if (shell.navNodes.length) return;
    void loadOroMxHomeCatalog()
      .then((catalog) => {
        shell.setNavNodes(catalog.nodes);
      })
      .catch(() => undefined);
  },
);

function go(to?: string) {
  shell.closeMenu();
  if (to && to !== route.path) {
    void router.push(to);
  }
}

function goPromo(to?: string, id?: string) {
  if (id === 'scratch') {
    shell.closeMenu();
    if (!sessionStore.isAuthenticated) {
      authModal.openAuth({ mode: 'login' });
      return;
    }
    shell.openScratch();
    return;
  }
  go(to);
}

function openSupport() {
  shell.closeMenu();
  void supportChat.open();
}

function openTelegram() {
  shell.closeMenu();
  void supportChat.openTelegram();
}

function onNav(item: (typeof oroMxMenuNav)[number]) {
  if (item.id === 'telegram') {
    openTelegram();
    return;
  }
  go(item.to);
}

function selectCasino(id: string) {
  shell.setLobbyTab(id);
  casinoOpen.value = false;
  go('/');
}
</script>

<template>
  <div class="side-menu-root" :class="{ 'is-open': shell.menuOpen }" :aria-hidden="!shell.menuOpen">
    <button
      v-if="shell.menuOpen"
      class="side-menu-header-hit"
      type="button"
      :aria-label="t('oro.menu.close')"
      @click="shell.closeMenu()"
    />
    <button
      class="side-menu-overlay"
      type="button"
      :aria-label="t('oro.menu.close')"
      @click="shell.closeMenu()"
    />
      <aside class="side-menu-panel" :aria-label="t('oro.tab.menu')">
        <div class="side-menu-scroll">
        <div class="side-menu-body">
          <div class="side-menu-promos">
            <button
              v-for="promo in oroMxMenuPromos"
              :key="promo.id"
              class="side-menu-promo-btn"
              type="button"
              @click="goPromo(promo.to, promo.id)"
            >
              <img class="side-menu-promo-img" :src="promo.image" alt="" loading="lazy" decoding="async" />
            </button>
          </div>

          <nav class="side-menu-nav" :aria-label="t('oro.tab.menu')">
            <div class="side-menu-nav-games">
              <button
                class="side-menu-nav-item side-menu-nav-item--accordion"
                :class="{ 'side-menu-nav-item--accordion-open': casinoOpen }"
                type="button"
                @click="casinoOpen = !casinoOpen"
              >
                <span class="side-menu-nav-link">
                  <img class="side-menu-nav-icon" :src="oroMxMenuAssets.navCasino" alt="" loading="lazy" decoding="async" />
                  <span class="side-menu-nav-label">{{ t('oro.menu.casino') }}</span>
                  <i
                    class="side-menu-nav-chevron"
                    :class="{ 'side-menu-nav-chevron--up': casinoOpen }"
                  />
                </span>
              </button>
              <div class="side-menu-games-sub" :class="{ 'is-open': casinoOpen }">
                <button
                  v-for="item in casinoSubs"
                  :key="item.id"
                  class="side-menu-games-sub-item"
                  :class="{ 'side-menu-games-sub-item--active': shell.lobbyTab === item.id }"
                  type="button"
                  @click="selectCasino(item.id)"
                >
                  <span class="side-menu-games-sub-icon">
                    <svg
                      v-if="item.iconKind === 'svg'"
                      class="side-menu-games-sub-img"
                      :viewBox="item.viewBox || '0 0 24 24'"
                      aria-hidden="true"
                    >
                      <use :href="`${sprite}#${item.icon}`" />
                    </svg>
                    <img v-else class="side-menu-games-sub-img" :src="item.icon" alt="" />
                  </span>
                  <span class="side-menu-games-sub-label">{{
                    isOroMxChromeTab(item.id) ? t(`oro.lobby.${item.id}`) : item.label
                  }}</span>
                </button>
              </div>
            </div>

            <button
              v-for="item in oroMxMenuNav"
              :key="item.id"
              class="side-menu-nav-item"
              type="button"
              @click="onNav(item)"
            >
              <span class="side-menu-nav-link">
                <img class="side-menu-nav-icon" :src="item.icon" alt="" loading="lazy" decoding="async" />
                <span class="side-menu-nav-label">{{ t(`oro.menu.${item.id}`) }}</span>
              </span>
            </button>

            <button class="side-menu-nav-item" type="button" @click="openSupport">
              <span class="side-menu-nav-link">
                <svg class="side-menu-nav-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M12 3.4c-4.7 0-8.5 3.72-8.5 8.3v.85c0 .64.52 1.15 1.15 1.15s1.15-.51 1.15-1.15V11.7c0-3.42 2.78-6.2 6.2-6.2s6.2 2.78 6.2 6.2v.85c0 .64.52 1.15 1.15 1.15s1.15-.51 1.15-1.15V11.7c0-4.58-3.8-8.3-8.5-8.3Z"
                  />
                  <rect fill="currentColor" x="1.85" y="10.85" width="4.55" height="7.35" rx="1.85" />
                  <rect fill="currentColor" x="17.6" y="10.85" width="4.55" height="7.35" rx="1.85" />
                  <path
                    fill="currentColor"
                    d="M18.05 16.55c0 2.95-2.75 5.05-6.35 5.05-.7 0-1.25-.55-1.25-1.25s.55-1.25 1.25-1.25c2.55 0 3.85-1.3 3.85-2.55h2.5Z"
                  />
                  <circle fill="currentColor" cx="11.7" cy="20.35" r="1.15" />
                </svg>
                <span class="side-menu-nav-label">{{ t('oro.menu.chat') }}</span>
              </span>
            </button>
          </nav>

          <footer class="side-menu-footer">
            <OroMxLangPicker />
          </footer>
        </div>
      </div>
    </aside>
  </div>
</template>

<style scoped>
.side-menu-root {
  position: absolute;
  inset: 0;
  z-index: 3100;
  overflow: hidden;
  pointer-events: none;
}

.side-menu-root.is-open .side-menu-header-hit,
.side-menu-root.is-open .side-menu-overlay,
.side-menu-root.is-open .side-menu-panel {
  pointer-events: auto;
}

.side-menu-header-hit {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 3;
  height: 3.2rem;
  padding: 0;
  background: transparent;
  border: 0;
}

.side-menu-overlay {
  position: absolute;
  inset: 3.2rem 0 3.3067rem;
  z-index: 1;
  display: none;
  padding: 0;
  background: rgb(0 0 0 / 55%);
  border: 0;
}

.side-menu-root.is-open .side-menu-overlay {
  display: block;
}

.side-menu-panel {
  position: absolute;
  top: 3.2rem;
  bottom: 3.3067rem;
  left: 0;
  z-index: 2;
  width: 100%;
  overflow: hidden;
  background: #232325;
  transform: translateX(-100%);
  transition: transform 0.28s;
}

.side-menu-root.is-open .side-menu-panel {
  transform: none;
}

.side-menu-scroll {
  height: 100%;
  overflow: hidden auto;
}

.side-menu-body {
  display: flex;
  flex-direction: column;
  padding: 0.5333rem 0.5333rem 0.8533rem;
}

.side-menu-promos {
  display: flex;
  flex-direction: column;
  gap: 0.2667rem;
  margin-bottom: 0.5333rem;
}

.side-menu-promo-btn {
  display: block;
  width: 18.9333rem;
  height: 3.2044rem;
  padding: 0;
  overflow: hidden;
  background: transparent;
  border: 0;
  border-radius: 0.32rem;
}

.side-menu-promo-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.side-menu-nav {
  display: flex;
  flex-direction: column;
  gap: 0.2133rem;
  margin-bottom: 0.64rem;
}

.side-menu-nav-item {
  display: block;
  width: 18.9333rem;
  height: 2.0267rem;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 0.2667rem;
}

.side-menu-nav-item--accordion {
  height: 2.0267rem;
}

.side-menu-nav-link {
  position: relative;
  display: flex;
  gap: 0.8rem;
  align-items: center;
  width: 100%;
  height: 2.0267rem;
  padding: 0 0.5333rem;
}

.side-menu-nav-icon {
  display: block;
  flex-shrink: 0;
  width: 1.28rem;
  height: 1.28rem;
  color: #fff;
  object-fit: contain;
}

.side-menu-nav-label {
  flex: 1 1 0;
  color: #fff;
  font-size: 0.8533rem;
  font-weight: 600;
  line-height: 1.28rem;
  text-align: left;
}

.side-menu-nav-chevron {
  display: block;
  flex-shrink: 0;
  box-sizing: border-box;
  width: 0.32rem;
  height: 0.32rem;
  border-right: 1px solid #fff;
  border-bottom: 1px solid #fff;
  transform: rotate(45deg);
}

.side-menu-nav-chevron--up {
  transform: rotate(-135deg);
}

.side-menu-games-sub {
  display: none;
}

.side-menu-games-sub.is-open {
  display: block;
}

.side-menu-games-sub-item {
  display: flex;
  gap: 0.64rem;
  align-items: center;
  width: 18.9333rem;
  height: 1.92rem;
  padding: 0 0.5333rem 0 2.6133rem;
  background: transparent;
  border: 0;
}

.side-menu-games-sub-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 1.0667rem;
  height: 1.0667rem;
}

.side-menu-games-sub-img {
  display: block;
  width: 0.96rem;
  height: 0.96rem;
  color: #fff;
  object-fit: contain;
  fill: currentColor;
}

.side-menu-games-sub-label {
  display: block;
  width: 14.08rem;
  color: #fff;
  font-size: 0.7467rem;
  font-weight: 600;
  line-height: 1.12rem;
  text-align: left;
}

.side-menu-footer {
  display: flex;
  flex-direction: column;
  gap: 0.4267rem;
  width: 18.9333rem;
}

.side-menu-footer :deep(.oro-lang),
.side-menu-footer :deep(.oro-lang__trigger) {
  width: 100%;
}

.side-menu-lang-trigger {
  display: flex;
  gap: 0.4267rem;
  align-items: center;
  width: 18.9333rem;
  height: 2.0267rem;
  padding: 0.4267rem 0.5333rem;
  color: #fff;
  font-size: 0.64rem;
  font-weight: 600;
  background: #15161c;
  border: 0;
  border-radius: 0.32rem;
}

.side-menu-lang-flag {
  display: block;
  width: 1.1733rem;
  height: 1.1733rem;
  object-fit: cover;
  border-radius: 0.1067rem;
}

.side-menu-lang-arrow {
  display: block;
  width: 0;
  height: 0;
  margin-left: auto;
  border-top: 0.3111rem solid #757577;
  border-right: 0.2667rem solid transparent;
  border-left: 0.2667rem solid transparent;
}
</style>
